import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon } from '@mui/icons-material';
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import PieLegendCard from "components/Visualizations/Piecharts"
import Barcharts from "components/Visualizations/Barcharts"
import SkuSingleBarChart from "components/Visualizations/SingleBarchartSku";
import FilterChip from "components/FilterChip/FilterChip";
import { fetchTechnology } from "services/technology.service"

import { getTimeSeriesLabels } from "../../../components/TimeSeries/monthUtils";

const TechnologyDashboard = ({ subcategoryToken: propSubcategoryToken }) => {

    const {
        lastYearSameMonth,
        currentMonthLabel,
        prevMonthLabel
    } = getTimeSeriesLabels();
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav } = controller;
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px

    // State for API data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [technologyData, setTechnologyData] = useState(null);

    useEffect(() => {
        const header = document.querySelector('.MuiAppBar-root');
        if (header) {
            header.style.display = 'none';
        }
    }, []);


    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                const technologyResponse = await fetchTechnology();
                console.log('Technology API Response:', technologyResponse);
                const technologyItem = technologyResponse || null;
                setTechnologyData(technologyItem);

            } catch (error) {
                console.error('Failed loading static mocks or transforming data:', error);
                setError('Failed to load static mock data. Check competitive_analysis/data/allMocks.json');
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [location.search, propSubcategoryToken]);

    const formatToMillions = (value) => {
        return value == null || value === "-" ? "-" : (Number(value) / 1_000_000).toFixed(1);
    }


    const truncateDecimal = (num, decimals = 1) => {
        const factor = Math.pow(10, decimals);
        return Math.floor(num * factor) / factor;
    };

    // KPI cards data
    const Kpiscardsdata = [
        {
            title: "Service Ticket Count",
            values: [
                {
                    label: "MTD",
                    change: technologyData?.ticketCount?.items?.cMvsGoal ? `${technologyData?.ticketCount?.items?.cMvsGoal}%` : "0%",
                    changely: technologyData?.ticketCount?.items?.cMvsLM ? `${technologyData?.ticketCount?.items?.cMvsLM}%` : "0%",
                    value: `${technologyData?.ticketCount?.items?.currentMonth}`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(technologyData?.ticketCount?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(technologyData?.ticketCount?.items?.goalsCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: technologyData?.ticketCount?.items?.cQvsGoal ? `${technologyData?.ticketCount?.items?.cQvsGoal}%` : "0%",
                    changely: technologyData?.ticketCount?.items?.cQvsLQ ? `${technologyData?.ticketCount?.items?.cQvsLQ}%` : "0%",
                    value: `${technologyData?.ticketCount?.items?.currentQuarter}`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(technologyData?.ticketCount?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(technologyData?.ticketCount?.items?.goalsCurrentQuarter)}M`

                },
                {
                    label: "YTD",
                    change: technologyData?.ticketCount?.items?.cYvsGoal ? `${technologyData?.ticketCount?.items?.cYvsGoal}%` : "0%",
                    changely: technologyData?.ticketCount?.items?.cYvsLY ? `${technologyData?.ticketCount?.items?.cYvsLY}%` : "0%",
                    value: `${technologyData?.ticketCount?.items?.currentYear}`,
                    status: "decrease",
                    title: "Year",
                    lyValue: `$${formatToMillions(technologyData?.ticketCount?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(technologyData?.ticketCount?.items?.goalsCurrentYear)}M`
                }
            ]
        },
        {
            title: "Mean Time To Resolve (MTTR)",
            values: [
                {
                    label: "MTD",
                    change: technologyData?.mttr?.items?.cMvsGoal ? `${technologyData?.mttr?.items?.cMvsGoal}%` : "0%",
                    changely: technologyData?.mttr?.items?.cMvsLM ? `${technologyData?.mttr?.items?.cMvsLM}%` : "0%",
                    value: `${((technologyData?.mttr?.items?.currentMonth ?? 0) / 60).toFixed(2)} hrs`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(technologyData?.mttr?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(technologyData?.mttr?.items?.goalsCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: technologyData?.mttr?.items?.cQvsGoal ? `${technologyData?.mttr?.items?.cQvsGoal}%` : "0%",
                    changely: technologyData?.mttr?.items?.cQvsLQ ? `${technologyData?.mttr?.items?.cQvsLQ}%` : "0%",
                    value: `${((technologyData?.mttr?.items?.currentQuarter ?? 0) / 60).toFixed(2)} hrs`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(technologyData?.mttr?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(technologyData?.mttr?.items?.goalsCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: technologyData?.mttr?.items?.cYvsGoal ? `${technologyData?.mttr?.items?.cYvsGoal}%` : "0%",
                    changely: technologyData?.mttr?.items?.cYvsLY ? `${technologyData?.mttr?.items?.cYvsLY}%` : "0%",
                    value: `${((technologyData?.mttr?.items?.currentYear ?? 0) / 60).toFixed(2)} hrs`,
                    status: "increase",
                    title: "Year",
                    lyValue: `$${formatToMillions(technologyData?.mttr?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(technologyData?.mttr?.items?.goalsCurrentYear)}M`

                }
            ]
        },
        {
            title: "System Uptime",
            values: [
                {
                    label: "MTD",
                    change: technologyData?.uptime?.items?.cMvsGoal ? `${technologyData?.uptime?.items?.cMvsGoal}%` : "0%",
                    changely: technologyData?.uptime?.items?.cMvsLM ? `${technologyData?.uptime?.items?.cMvsLM}%` : "0%",
                    value: `${((((technologyData?.uptime?.items?.currentMonth ?? 0) / 60) * 100) || 0)
                        .toFixed(1)
                        .replace("0.0", "0")}%`
                    ,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(technologyData?.uptime?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(technologyData?.uptime?.items?.goalsCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: technologyData?.uptime?.items?.cQvsGoal ? `${technologyData?.uptime?.items?.cQvsGoal}%` : "0%",
                    changely: technologyData?.uptime?.items?.cQvsLQ ? `${technologyData?.uptime?.items?.cQvsLQ}%` : "0%",
                    value: `${((((technologyData?.uptime?.items?.currentQuarter ?? 0) / 60) * 100) || 0)
                        .toFixed(1)
                        .replace("0.0", "0")}%`
                    ,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(technologyData?.uptime?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(technologyData?.uptime?.items?.goalsCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: technologyData?.uptime?.items?.cYvsGoal ? `${technologyData?.uptime?.items?.cYvsGoal}%` : "0%",
                    changely: technologyData?.uptime?.items?.cYvsLY ? `${technologyData?.uptime?.items?.cYvsLY}%` : "0%",
                    value: `${((((technologyData?.uptime?.items?.currentYear ?? 0) / 60) * 100) || 0)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Year",
                    lyValue: `$${formatToMillions(technologyData?.uptime?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(technologyData?.uptime?.items?.goalsCurrentYear)}M`
                }
            ]
        },
        {
            title: "Cyber Security Incidents",
            values: [
                {
                    label: "MTD",
                    change: technologyData?.securityIncidents?.items?.cMvsGoal ? `${technologyData?.securityIncidents?.items?.cMvsGoal}%` : "0%",
                    changely: technologyData?.securityIncidents?.items?.cMvsLM ? `${technologyData?.securityIncidents?.items?.cMvsLM}%` : "0%",
                    value: `${technologyData?.securityIncidents?.items?.currentMonth}`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(technologyData?.securityIncidents?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(technologyData?.securityIncidents?.items?.goalsCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: technologyData?.securityIncidents?.items?.cQvsGoal ? `${technologyData?.securityIncidents?.items?.cQvsGoal}%` : "0%",
                    changely: technologyData?.securityIncidents?.items?.cQvsLQ ? `${technologyData?.securityIncidents?.items?.cQvsLQ}%` : "0%",
                    value: `${technologyData?.securityIncidents?.items?.currentQuarter}`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(technologyData?.securityIncidents?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(technologyData?.securityIncidents?.items?.goalsCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: technologyData?.securityIncidents?.items?.cYvsGoal ? `${technologyData?.securityIncidents?.items?.cYvsGoal}%` : "0%",
                    changely: technologyData?.securityIncidents?.items?.cYvsLY ? `${technologyData?.securityIncidents?.items?.cYvsLY}%` : "0%",
                    value: `${technologyData?.securityIncidents?.items?.currentYear}`,
                    status: "decrease",
                    title: "Year",
                    lyValue: `$${formatToMillions(technologyData?.securityIncidents?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(technologyData?.securityIncidents?.items?.goalsCurrentYear)}M`
                }
            ]
        },
        {
            title: "Average Response Time",
            values: [
                {
                    label: "MTD",
                    change: technologyData?.responseTime?.items?.cMvsGoal ? `${technologyData?.responseTime?.items?.cMvsGoal}%` : "0%",
                    changely: technologyData?.responseTime?.items?.cMvsLM ? `${technologyData?.responseTime?.items?.cMvsLM}%` : "0%",
                    value: `${Math.round((technologyData?.responseTime?.items?.currentMonth ?? 0) / 60)} mins`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(technologyData?.responseTime?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(technologyData?.responseTime?.items?.goalsCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: technologyData?.responseTime?.items?.cQvsGoal ? `${technologyData?.responseTime?.items?.cQvsGoal}%` : "0%",
                    changely: technologyData?.responseTime?.items?.cQvsLQ ? `${technologyData?.responseTime?.items?.cQvsLQ}%` : "0%",
                    value: `${Math.round((technologyData?.responseTime?.items?.currentQuarter ?? 0) / 60)} mins`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(technologyData?.responseTime?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(technologyData?.responseTime?.items?.goalsCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: technologyData?.responseTime?.items?.cYvsGoal ? `${technologyData?.responseTime?.items?.cYvsGoal}%` : "0%",
                    changely: technologyData?.responseTime?.items?.cYvsLY ? `${technologyData?.responseTime?.items?.cYvsLY}%` : "0%",
                    value: `${Math.round((technologyData?.responseTime?.items?.currentYear ?? 0) / 60)} mins`,
                    status: "decrease",
                    title: "Year",
                    lyValue: `$${formatToMillions(technologyData?.responseTime?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(technologyData?.responseTime?.items?.goalsCurrentYear)}M`
                }
            ]
        },
        {
            title: "First Contact Resolution (FCR)",
            values: [
                {
                    label: "MTD",
                    change: technologyData?.fcr?.items?.cMvsGoal ? `${technologyData?.fcr?.items?.cMvsGoal}%` : "0%",
                    changely: technologyData?.fcr?.items?.cMvsLM ? `${technologyData?.fcr?.items?.cMvsLM}%` : "0%",
                    value: `${((((technologyData?.fcr?.items?.currentMonth ?? 0) / 60) * 100) || 0)
                        .toFixed(1)
                        .replace("0.0", "0")}%`
                    ,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(technologyData?.fcr?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(technologyData?.fcr?.items?.goalsCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: technologyData?.fcr?.items?.cQvsGoal ? `${technologyData?.fcr?.items?.cQvsGoal}%` : "0%",
                    changely: technologyData?.fcr?.items?.cQvsLQ ? `${technologyData?.fcr?.items?.cQvsLQ}%` : "0%",
                    value: `${((((technologyData?.fcr?.items?.currentQuarter ?? 0) / 60) * 100) || 0)
                        .toFixed(1)
                        .replace("0.0", "0")}%`
                    ,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(technologyData?.fcr?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(technologyData?.fcr?.items?.goalsCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: technologyData?.fcr?.items?.cYvsGoal ? `${technologyData?.fcr?.items?.cYvsGoal}%` : "0%",
                    changely: technologyData?.fcr?.items?.cYvsLY ? `${technologyData?.fcr?.items?.cYvsLY}%` : "0%",
                    value: `${((((technologyData?.fcr?.items?.currentYear ?? 0) / 60) * 100) || 0)
                        .toFixed(1)
                        .replace("0.0", "0")}%`
                    ,
                    status: "increase",
                    title: "Year",
                    lyValue: `$${formatToMillions(technologyData?.fcr?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(technologyData?.fcr?.items?.goalsCurrentYear)}M`
                }
            ]
        },
        {
            title: "SLA Compliance Rate",
            values: [
                {
                    label: "MTD",
                    change: technologyData?.sla?.items?.cMvsGoal ? `${technologyData?.sla?.items?.cMvsGoal}%` : "0%",
                    changely: technologyData?.sla?.items?.cMvsLM ? `${technologyData?.sla?.items?.cMvsLM}%` : "0%",
                    value: `${((((technologyData?.sla?.items?.currentMonth ?? 0) / 60) * 100) || 0)
                        .toFixed(1)
                        .replace("0.0", "0")}%`
                    ,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(technologyData?.sla?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(technologyData?.sla?.items?.goalsCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: technologyData?.sla?.items?.cQvsGoal ? `${technologyData?.sla?.items?.cQvsGoal}%` : "0%",
                    changely: technologyData?.sla?.items?.cQvsLQ ? `${technologyData?.sla?.items?.cQvsLQ}%` : "0%",
                    value: `${((((technologyData?.sla?.items?.currentQuarter ?? 0) / 60) * 100) || 0)
                        .toFixed(1)
                        .replace("0.0", "0")}%`
                    ,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(technologyData?.sla?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(technologyData?.sla?.items?.goalsCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: technologyData?.sla?.items?.cYvsGoal ? `${technologyData?.sla?.items?.cYvsGoal}%` : "0%",
                    changely: technologyData?.sla?.items?.cYvsLY ? `${technologyData?.sla?.items?.cYvsLY}%` : "0%",
                    value: `${((((technologyData?.sla?.items?.currentYear ?? 0) / 60) * 100) || 0)
                        .toFixed(1)
                        .replace("0.0", "0")}%`
                    ,
                    status: "increase",
                    title: "Year",
                    lyValue: `$${formatToMillions(technologyData?.sla?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(technologyData?.sla?.items?.goalsCurrentYear)}M`
                }
            ]
        },
        {
            title: "Urgent Priority Tickets",
            values: [
                {
                    label: "MTD",
                    change: technologyData?.urgentTickets?.items?.cMvsGoal ? `${technologyData?.urgentTickets?.items?.cMvsGoal}%` : "0%",
                    changely: technologyData?.urgentTickets?.items?.cMvsLM ? `${technologyData?.urgentTickets?.items?.cMvsLM}%` : "0%",
                    value: `${technologyData?.urgentTickets?.items?.currentMonth}`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(technologyData?.urgentTickets?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(technologyData?.urgentTickets?.items?.goalsCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: technologyData?.urgentTickets?.items?.cQvsGoal ? `${technologyData?.urgentTickets?.items?.cQvsGoal}%` : "0%",
                    changely: technologyData?.urgentTickets?.items?.cQvsLQ ? `${technologyData?.urgentTickets?.items?.cQvsLQ}%` : "0%",
                    value: `${technologyData?.urgentTickets?.items?.currentQuarter}`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(technologyData?.urgentTickets?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(technologyData?.urgentTickets?.items?.goalsCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: technologyData?.urgentTickets?.items?.cYvsGoal ? `${technologyData?.urgentTickets?.items?.cYvsGoal}%` : "0%",
                    changely: technologyData?.urgentTickets?.items?.cYvsLY ? `${technologyData?.urgentTickets?.items?.cYvsLY}%` : "0%",
                    value: `${technologyData?.urgentTickets?.items?.currentYear}`,
                    status: "decrease",
                    title: "Year",
                    lyValue: `$${formatToMillions(technologyData?.urgentTickets?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(technologyData?.urgentTickets?.items?.goalsCurrentYear)}M`
                }
            ]
        },
        {
            title: "Average Tickets per Day",
            values: [
                {
                    label: "MTD",
                    change: technologyData?.ticketsPerDay?.items?.cMvsGoal ? `${technologyData?.ticketsPerDay?.items?.cMvsGoal}%` : "0%",
                    changely: technologyData?.ticketsPerDay?.items?.cMvsLM ? `${technologyData?.ticketsPerDay?.items?.cMvsLM}%` : "0%",
                    value: `${technologyData?.ticketsPerDay?.items?.currentMonth}`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(technologyData?.ticketsPerDay?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(technologyData?.ticketsPerDay?.items?.goalsCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: technologyData?.ticketsPerDay?.items?.cQvsGoal ? `${technologyData?.ticketsPerDay?.items?.cQvsGoal}%` : "0%",
                    changely: technologyData?.ticketsPerDay?.items?.cQvsLQ ? `${technologyData?.ticketsPerDay?.items?.cQvsLQ}%` : "0%",
                    value: `${technologyData?.ticketsPerDay?.items?.currentQuarter}`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(technologyData?.ticketsPerDay?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(technologyData?.ticketsPerDay?.items?.goalsCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: technologyData?.ticketsPerDay?.items?.cYvsGoal ? `${technologyData?.ticketsPerDay?.items?.cYvsGoal}%` : "0%",
                    changely: technologyData?.ticketsPerDay?.items?.cYvsLY ? `${technologyData?.ticketsPerDay?.items?.cYvsLY}%` : "0%",
                    value: `${technologyData?.ticketsPerDay?.items?.currentYear}`,
                    status: "decrease",
                    title: "Year",
                    lyValue: `$${formatToMillions(technologyData?.ticketsPerDay?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(technologyData?.ticketsPerDay?.items?.goalsCurrentYear)}M`
                }
            ]
        }
    ];

    console.log(technologyData?.uptime?.items?.currentMonth, 'timeee series')

    //second section cards
    const timeSeriesCardsdata = [
        {
            title: "Service Ticket Count",
            values: [
                {
                    label: lastYearSameMonth,
                    change: technologyData?.ticketCount?.items?.cmcYvsCMLY ? `${truncateDecimal(technologyData?.ticketCount?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.ticketCount?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(technologyData?.ticketCount?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: technologyData?.ticketCount?.items?.cMvsLM ? `${truncateDecimal(technologyData?.ticketCount?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.ticketCount?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },

        {
            title: "Mean Time To Resolve",
            values: [
                {
                    label: lastYearSameMonth,
                    change: technologyData?.mttr?.items?.cmcYvsCMLY ? `${truncateDecimal(technologyData?.mttr?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.mttr?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(technologyData?.mttr?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: technologyData?.mttr?.items?.cMvsLM ? `${truncateDecimal(technologyData?.mttr?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.mttr?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },

        {
            title: "System Uptime",
            values: [
                {
                    label: lastYearSameMonth,
                    change: technologyData?.uptime?.items?.cmcYvsCMLY ? `${truncateDecimal(technologyData?.uptime?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.uptime?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(technologyData?.uptime?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: technologyData?.uptime?.items?.cMvsLM ? `${truncateDecimal(technologyData?.uptime?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.uptime?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },

        {
            title: "Cyber Security Incidents",
            values: [
                {
                    label: lastYearSameMonth,
                    change: technologyData?.securityIncidents?.items?.cmcYvsCMLY ? `${truncateDecimal(technologyData?.securityIncidents?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.securityIncidents?.items?.sameMonthLastYear)}M`,
                    status: "decrease",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(technologyData?.securityIncidents?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: technologyData?.securityIncidents?.items?.cMvsLM ? `${truncateDecimal(technologyData?.securityIncidents?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.securityIncidents?.items?.lastMonth)}M`,
                    status: "decrease",
                    arrow: "left"
                }
            ]
        },

        {
            title: "Average Response Time",
            values: [
                {
                    label: lastYearSameMonth,
                    change: technologyData?.responseTime?.items?.cmcYvsCMLY ? `${truncateDecimal(technologyData?.responseTime?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.responseTime?.items?.sameMonthLastYear)}M`,
                    status: "decrease",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(technologyData?.responseTime?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: technologyData?.responseTime?.items?.cMvsLM ? `${truncateDecimal(technologyData?.responseTime?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.responseTime?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },

        {
            title: "First Contact Resolution (FCR)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: technologyData?.fcr?.items?.cmcYvsCMLY ? `${truncateDecimal(technologyData?.fcr?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.fcr?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(technologyData?.fcr?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: technologyData?.fcr?.items?.cMvsLM ? `${truncateDecimal(technologyData?.fcr?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.fcr?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },

        {
            title: "SLA Compliance Rate",
            values: [
                {
                    label: lastYearSameMonth,
                    change: technologyData?.sla?.items?.cmcYvsCMLY ? `${truncateDecimal(technologyData?.sla?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.sla?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(technologyData?.sla?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: technologyData?.sla?.items?.cMvsLM ? `${truncateDecimal(technologyData?.sla?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.sla?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },

        {
            title: "Urgent Priority Tickets",
            values: [
                {
                    label: lastYearSameMonth,
                    change: technologyData?.urgentTickets?.items?.cmcYvsCMLY ? `${truncateDecimal(technologyData?.urgentTickets?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.urgentTickets?.items?.sameMonthLastYear)}M`,
                    status: "decrease",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(technologyData?.urgentTickets?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: technologyData?.urgentTickets?.items?.cMvsLM ? `${truncateDecimal(technologyData?.urgentTickets?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.urgentTickets?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },

        {
            title: "Average Tickets per Day",
            values: [
                {
                    label: lastYearSameMonth,
                    change: technologyData?.ticketsPerDay?.items?.cmcYvsCMLY ? `${truncateDecimal(technologyData?.ticketsPerDay?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.ticketsPerDay?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: "",
                    value: `$${formatToMillions(technologyData?.ticketsPerDay?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: technologyData?.ticketsPerDay?.items?.cMvsLM ? `${truncateDecimal(technologyData?.ticketsPerDay?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(technologyData?.ticketsPerDay?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        }
    ];




    const [openFilters, setOpenFilters] = useState(false);
    const handleToggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

    // Filter state
    const [activeFilter, setActiveFilter] = useState('All');
    const [filterLoading, setFilterLoading] = useState(false);

    // Define filter presets for each quick filter
    const getFilterPreset = useCallback((filterName) => {
        const allCompanies = ["Nutrabolt", "Bloom"];
        const allCategories = ["Functional Beverage", "Active Nutrition"];
        const allBrands = ["C4", "Cellucor", "XTEND", "Bloom"];
        const allSubBrands = ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND", "Bloom Sparkling Energy", "Bloom Pop"];
        const allRoutes = ["Ecommerce", "Direct", "DSD"];
        const allChannels = ["Ecommerce", "Convenience Chain", "Convenience Independent", "Drug", "Food", "Multi Outlet", "Mass"];

        switch (filterName) {
            case 'All':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Corporate':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Systems':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Custom Apps':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            default:
                return null;
        }
    }, []);

    const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));

    // Handle filter click with full-page loader
    const handleFilterClick = (filter) => {
        if (filter === activeFilter || filterLoading) return;

        setFilterLoading(true);
        setLoading(true); // Show full-page loader

        // Simulate loading delay
        setTimeout(() => {
            const preset = getFilterPreset(filter);
            setActiveFilter(filter);

            // Set the preset - this will trigger FilterPanelContainer to apply it
            if (preset) {
                setFilterPresets(preset);
            } else {
                setFilterPresets(null);
            }

            setFilterLoading(false);
            setLoading(false); // Hide full-page loader

            // Open filter panel for "Custom" filter
            if (filter === 'Custom') {
                setOpenFilters(true);
            }
        }, 800);
    };

    // Handle filter changes from FilterPanelContainer
    const handleFiltersChange = useCallback((newFilters) => {
        // Don't check for preset match if we're already in Custom mode
        // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
        if (activeFilter === 'Custom') return;

        // Check if the current filters match any preset
        const presetNames = ['All', 'Corporate', 'Systems', 'Custom Apps'];
        let matchedPreset = null;

        for (const presetName of presetNames) {
            const preset = getFilterPreset(presetName);
            if (preset) {
                const isMatching = Object.keys(preset).every(key => {
                    const presetValues = [...(preset[key] || [])].sort();
                    const currentValues = [...(newFilters[key] || [])].sort();
                    return presetValues.length === currentValues.length &&
                        presetValues.every((val, idx) => val === currentValues[idx]);
                });

                if (isMatching) {
                    matchedPreset = presetName;
                    break;
                }
            }
        }

        // If filters don't match any preset, switch to Custom
        if (!matchedPreset && activeFilter !== 'Custom') {
            setActiveFilter('Custom');
            setFilterPresets(null);
        }
    }, [activeFilter, getFilterPreset]);

    const [activeIdx, setActiveIdx] = React.useState(0);

    const getTicketStatusColor = (statusName) => {
        const colorMap = {
            Escalated: "#D93025",
            Resolved: "#34A853",
            Open: "#1A73E8",
            Pending: "#F6C344",
        };

        return colorMap[statusName] || "#000000";
    };

    const getTicketPriorityColor = (priorityName) => {
        const colorMap = {
            High: "#EB1B1B",
            Urgent: "#000000",
            Low: "#1FA5EC",
            Medium: "#FE9423",
        };

        return colorMap[priorityName] || "#000000";
    };

    const getTicketImpactColor = (impactName) => {
        const colorMap = {
            "Business Intelligence": "#F6C344",
            "Integrations": "#34A853",
            "Other": "#000000",
            "Custom Apps": "#1A73E8",
            "Dynamics 365": "#5C2D91",
        };

        return colorMap[impactName] || "#000000";
    };

    const getTicketClassificationColor = (classificationName) => {
        const colorMap = {
            "Request": "#1A73E8",
            "Question": "#F6C344",
            "Task": "#5F6368",
            "Enhancement": "#34A853",
            "Problem": "#B80672",
            "Incident": "#D93025",
            "Project": "#5C2D91",
        };

        return colorMap[classificationName] || "#000000";
    };

    const ticketStatus = useMemo(() => {
        const items = technologyData?.ticketsByStatus?.items;

        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }

        return items.map((item) => ({
            label: item.category || "",
            value: item.ytdPct ?? 0,   // Use ytdPct instead of percentage
            color: getTicketStatusColor(item.category)
        }));
    }, [technologyData]);

    console.log(ticketStatus, "ticketStatus")

    const ticketPriority = useMemo(() => {
        const items = technologyData?.ticketsByPriority?.items;

        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }

        return items.map((item) => ({
            label: item.category || "",
            value: item.ytdPct ?? 0,   // Use ytdPct instead of percentage
            color: getTicketPriorityColor(item.category)
        }));
    }, [technologyData]);
    console.log(ticketPriority, "ticketPriority")

    const ticketImpact = useMemo(() => {
        const items = technologyData?.ticketsByImpactCategory?.items;

        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }

        return items.map((item) => ({
            label: item.category || "",
            value: item.ytdPct ?? 0,   // Use ytdPct instead of percentage
            color: getTicketImpactColor(item.category)
        }));
    }, [technologyData]);
    console.log(ticketImpact, "ticketImpact")

    const ticketClassification = useMemo(() => {
        const items = technologyData?.ticketsByClassification?.items;
        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }
        return items.map((item) => ({
            label: item.category || "",
            value: item.ytdPct ?? 0,   // Use ytdPct instead of percentage
            color: getTicketClassificationColor(item.category)
        }));
    }, [technologyData]);
    console.log(ticketClassification, "ticketClassification")




    const getTicketChartColor = (label) => {
        const colorMap = {
            "Custom Apps": "#1A73E8",
            "Dynamics 365": "#5C2D91",
            "Business Intelligence": "#F6C344",
            "Integrations": "#34A853",
            "Other": "#000000",
        };

        return colorMap[label] || "#000000";
    };

    const convertTicketCountByMonth = useMemo(() => {
        const items = technologyData?.ticketCountByMonth?.items || [];
        if (!items.length) return null;

        // Sort months
        const sortedItems = [...items].sort(
            (a, b) => new Date(`01-${a.month}`) - new Date(`01-${b.month}`)
        );

        // X-axis labels
        const xLabels = sortedItems.map(item =>
            item.month.replace("-", ". ")
        );

        // Collect unique appGroups
        const labelSet = new Set();
        sortedItems.forEach(month =>
            month.values.forEach(v => labelSet.add(v.appGroup))
        );

        const labels = Array.from(labelSet);

        //  Build series
        const series = labels.map(label => ({
            label,
            color: getTicketChartColor(label),
        }));

        // Build dataMatrix
        const dataMatrix = sortedItems.map(monthItem => {
            const valueMap = {};

            monthItem.values.forEach(v => {
                valueMap[v.appGroup] = v.value;
            });

            return labels.map(label => valueMap[label] || 0);
        });
        // Calculate stacked totals per month
        const stackedTotals = dataMatrix.map(row =>
            row.reduce((sum, val) => sum + val, 0)
        );

        // Get maximum stacked value
        const maxValue = Math.max(...stackedTotals);

        // Generate nice tick step
        function niceTickStep(maxVal, numTicks = 5) {
            const roughStep = maxVal / numTicks;
            const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
            const residual = roughStep / magnitude;

            let niceResidual;
            if (residual >= 5) niceResidual = 5;
            else if (residual >= 2) niceResidual = 2;
            else niceResidual = 1;

            return niceResidual * magnitude;
        }

        const tickStep = niceTickStep(maxValue, 5);
        const maxTick = tickStep * Math.ceil(maxValue / tickStep);

        const yTicks = [];
        for (let tick = 0; tick <= maxTick; tick += tickStep) {
            yTicks.push(tick);
        }

        return {
            title: "Ticket Count by Month",
            xLabels,
            series,
            dataMatrix,
            yTicks
        };
    }, [technologyData]);

    console.log(convertTicketCountByMonth, "convertTicketCountByMonth")


    const getPriorityColor = (priority) => {
        const colorMap = {
            Low: "#29A3F0",
            Medium: "#FF9F2D",
            High: "#E31A1C",
            Urgent: "#000000"
        };

        return colorMap[priority] || "#000000";
    };

    const ticketCountCustomApps1 = useMemo(() => {
        const items = technologyData?.top10CustomApps?.items;

        if (!Array.isArray(items) || items.length === 0) return null;

        // Y Axis Labels (category1 = Technology Names)
        const yLabels = [...new Set(items.map((i) => i.category1))];

        // Series Labels (category2 = Priority Levels)
        const priorityLabels = [...new Set(items.map((i) => i.category2))];

        // Priority Order (Optional fixed order)
        const priorityOrder = ["Low", "Medium", "High", "Urgent"];

        const sortedPriorities = priorityOrder.filter((p) =>
            priorityLabels.includes(p)
        );

        // Series with Colors
        const series = sortedPriorities.map((p) => ({
            label: p,
            color: getPriorityColor(p)
        }));

        // DataMatrix
        const dataMatrix = yLabels.map((tech) => {
            return sortedPriorities.map((priority) => {
                const match = items.find(
                    (i) => i.category1 === tech && i.category2 === priority
                );

                return match ? match.ytdValue : 0;
            });
        });
        const rowTotals = dataMatrix.map((row) =>
            row.reduce((sum, value) => sum + value, 0)
        );

        // Get maximum total
        const maxValue = Math.max(...rowTotals);

        // Round nicely to nearest 50
        const roundedMax = Math.ceil(maxValue / 50) * 50;

        // Create 5 divisions
        const divisions = 5;
        const step = roundedMax / divisions;

        const xTicks = [];
        for (let i = 0; i <= divisions; i++) {
            xTicks.push(i * step);
        }

        return {
            title: "Ticket Count by Top 10 Custom Apps",
            yLabels,
            series,
            dataMatrix,
            xTicks
        };
    }, [
        technologyData
    ])

    console.log(ticketCountCustomApps1, "ticketCountCustomApps")


    const ticketCountTopSubmittersChart = useMemo(() => {
        const items = technologyData?.top10Submitters?.items;
        if (!Array.isArray(items) || items.length === 0) return null;

        // Fixed priority order (important for consistent bars)
        const priorityOrder = ["Low", "Medium", "High", "Urgent"];

        // Y-axis labels (submitter names)
        const yLabels = [...new Set(items.map(i => i.category1))];

        // Series
        const series = priorityOrder.map(p => ({
            label: p,
            color: getPriorityColor(p),
        }));

        // Data matrix
        const dataMatrix = yLabels.map(submitter =>
            priorityOrder.map(priority => {
                const match = items.find(
                    i =>
                        i.category1 === submitter &&
                        i.category2 === priority
                );
                return match?.ytdValue ?? 0;
            })
        );
        const rowTotals = dataMatrix.map((row) =>
            row.reduce((sum, value) => sum + value, 0)
        );

        // Get maximum total
        const maxValue = Math.max(...rowTotals);

        // Round nicely to nearest 50
        const roundedMax = Math.ceil(maxValue / 50) * 50;

        // Create 5 divisions
        const divisions = 5;
        const step = roundedMax / divisions;

        const xTicks = [];
        for (let i = 0; i <= divisions; i++) {
            xTicks.push(i * step);
        }

        return {
            title: "Ticket Count by Top 10 Submitters",
            yLabels,
            series,
            dataMatrix,
            xTicks
        };
    }, [technologyData]);


    console.log(ticketCountTopSubmittersChart, "ticketCountTopSubmittersChart")




    return (


        <Box sx={{
            ml: 0,
            transition: "margin-left 0.25s ease",
            "& .MuiBox-root": { backgroundImage: "none" }
        }} style={{

            padding: isMobile ? '8px' : '0px 16px 0px 16px',
            fontFamily: 'inter !important'
        }}>
            {/* Loading Backdrop */}
            <Backdrop
                open={loading}
                sx={{
                    position: 'fixed',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <CircularProgress color="primary" />
            </Backdrop>

            <Box
                sx={{
                    height: {
                        xs: "calc(100dvh - 140px)", // mobile header
                        md: "calc(100vh - 64px)",  // desktop header
                    },
                    overflowY: "auto",
                    // px: isMobile ? 1 : 2,
                    pb: "100px",
                    /* smooth scrolling */
                    scrollBehavior: "smooth",
                    WebkitOverflowScrolling: "touch", // iOS momentum

                    /* hide scrollbar */
                    scrollbarWidth: "none",          // Firefox
                    msOverflowStyle: "none",         // IE / Edge
                    "&::-webkit-scrollbar": {
                        display: "none",               // Chrome / Safari
                    },
                }}
            >

                {/* Section Title: KPI's */}
                <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Technology / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>KPI's</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <KpiMetricscards cardsData={Kpiscardsdata} />

                {/* Section Title: Time Series */}
                <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Technology / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>Time Series</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '16px' }}>
                            <span style={{ color: "#1672B980", fontWeight: 200, fontSize: "16px" }}>Technology / </span> <span style={{ fontSize: "16px", fontWeight: 200 }}>Visualizations</span>
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {["YTD", "QTD", "MTD"].map((label, idx) => (
                                <FilterChip
                                    key={label}
                                    label={label}
                                    idx={idx}
                                    activeIdx={activeIdx}
                                    onClick={setActiveIdx}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                </Box>
                <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    sx={{
                        mt: "26px !important",
                        alignItems: "stretch"   // keeps all cards same height
                    }}
                >
                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
                    }}   >
                        <PieLegendCard title="Active Tickets by Status" data={ticketStatus} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Ticket Volume by Priority" data={ticketPriority} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Ticket Volume by Impact Category" data={ticketImpact} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Ticket Volume by Classification" data={ticketClassification} />
                    </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: '34px !important' }}>
                    {/* LEFT CARD - USA MAP */}
                    <Grid item xs={12} md={6} sx={{
                        pt: { xs: "0px !important", sm: "0px !important", }
                    }}>
                        {ticketCountCustomApps1?.title &&
                            ticketCountCustomApps1?.yLabels?.length > 0 &&
                            ticketCountCustomApps1?.series?.length > 0 &&
                            ticketCountCustomApps1?.dataMatrix?.length > 0 && (
                                <SkuSingleBarChart
                                    title={ticketCountCustomApps1.title}
                                    yLabels={ticketCountCustomApps1.yLabels}
                                    series={ticketCountCustomApps1.series}
                                    dataMatrix={ticketCountCustomApps1.dataMatrix}
                                    xTicks={ticketCountCustomApps1?.xTicks}
                                    showUnit={false}
                                />
                            )}


                    </Grid>

                    {/* RIGHT CARD - BAR CHART */}
                    <Grid item xs={12} md={6} sx={{
                        pt: { xs: "34px !important", sm: "34px !important", md: '0px !important' }
                    }}>
                        {ticketCountTopSubmittersChart?.title &&
                            ticketCountTopSubmittersChart?.yLabels?.length > 0 &&
                            ticketCountTopSubmittersChart?.series?.length > 0 &&
                            ticketCountTopSubmittersChart?.dataMatrix?.length > 0 && (
                                <SkuSingleBarChart
                                    title={ticketCountTopSubmittersChart.title}
                                    yLabels={ticketCountTopSubmittersChart.yLabels}
                                    series={ticketCountTopSubmittersChart.series}
                                    dataMatrix={ticketCountTopSubmittersChart.dataMatrix}
                                    xTicks={ticketCountTopSubmittersChart?.xTicks}
                                    showUnit={false}
                                />
                            )}
                    </Grid>
                </Grid>

                <Grid container sx={{ mt: '34px !important' }}>
                    <Grid item xs={12}>
                        {convertTicketCountByMonth?.title &&
                            convertTicketCountByMonth?.xLabels?.length > 0 &&
                            convertTicketCountByMonth?.series?.length > 0 &&
                            convertTicketCountByMonth?.dataMatrix?.length > 0 && (
                                <Barcharts
                                    title={convertTicketCountByMonth.title}
                                    xLabels={convertTicketCountByMonth.xLabels}
                                    series={convertTicketCountByMonth.series}
                                    dataMatrix={convertTicketCountByMonth.dataMatrix}
                                    yTicks={convertTicketCountByMonth?.yTicks}
                                    rounded={false}
                                    yAxisLabel="Count"
                                    valueFormatter={(v) => `${v}`}
                                />
                            )}

                    </Grid>
                </Grid>
            </Box>
        </Box>

    );
};

export default TechnologyDashboard;